import {
  ApprovalStatus,
  ReviewType,
  SourceString,
  SourceStringInput,
  SourceStringVariant,
  StringGroup,
} from 'src/generated/graphql';

/**
 * Get the string PK based on the string type.
 */
function getStringPK(sourceString: SourceString): string {
  if (sourceString.stringKey?.stringType == 'VARIANT') {
    return getVariantStringPK(sourceString);
  }

  return getSourceStringPK(sourceString);
}

function isInDevStage(stage: string): boolean {
  return stage == 'development' || stage == 'test' || stage == 'beta';
}

/**
 * Get the source string PK
 */
function getSourceStringPK(sourceString: SourceString): string {
  return `SOURCE#${sourceString.stringKey?.stringName}`;
}

/**
 * Get the variant string PK
 */
function getVariantStringPK(sourceString: SourceString): string {
  return `VARIANT#${sourceString.stringKey!.stringName}${
    sourceString.stringKey!.variant
      ? '#' +
        variantToString(
          sourceString.stringKey!.variant as SourceStringVariant[]
        )
      : ''
  }`;
}

function variantToString(variant: SourceStringVariant[]): string {
  return variant.map((v) => v?.key + ':' + v?.value).join(';');
}

/**
 * Convert the edited contents to the SourceStringInput used to update string content.
 * If any variants are updated, also include source string no matter if they are updated.
 *
 * @param editedContents {stringId: {stringId, sourceText, context, reviewComment, stringPK}}
 * @param stringResources the map containing stringPK to the original source string
 * @param reviewer optional reviewer alias who edited the content
 */
function toSourceStringInputs(
  editedContents: Record<string, any>,
  stringResources: Map<string, SourceString>,
  reviewer: string | null
): SourceStringInput[] {
  // generate the map from stringPK to sourceStringInput
  const updatedSourceStringMap = new Map(
    Object.entries(editedContents).map(([_, editedContent]) => [
      editedContent.stringPK,
      toSourceStringInput(
        stringResources.get(editedContent.stringPK),
        editedContent,
        reviewer
      ),
    ])
  );

  // include missing source string if any variants are updated
  const missingSourceStrings: Set<string> = new Set();
  for (const sourceString of updatedSourceStringMap.values()) {
    const sourceStringId = getSourceStringPK(sourceString);
    if (
      sourceString.stringKey?.stringType == 'VARIANT' &&
      !updatedSourceStringMap.has(sourceStringId)
    ) {
      missingSourceStrings.add(sourceStringId);
    }
  }

  // add the missing source strings to the updatedSourceStringMap
  missingSourceStrings.forEach((stringPK) => {
    updatedSourceStringMap.set(
      stringPK,
      toSourceStringInput(stringResources.get(stringPK))
    );
  });

  return [...updatedSourceStringMap.values()];
}

/**
 * Convert the {@link SourceString} to {@link SourceStringInput}. Merge the updated content if editedContent is not null.
 * @param sourceString the source string
 * @param editedContents optional {stringId, sourceText, context, reviewComment, stringPK}
 * @reviewer the optional reviewer who edited the content
 */
function toSourceStringInput(
  sourceString: SourceString | undefined,
  editedContent: any | undefined = undefined,
  reviewer: string | null = null
): SourceStringInput {
  if (!sourceString) {
    return {};
  }

  let variantKey = undefined;
  if (sourceString.stringKey?.variant) {
    variantKey = sourceString.stringKey.variant.map((variant) => {
      if (!variant) return null;
      return {
        key: variant.key,
        value: variant.value,
      };
    });
  }

  let stringKey = undefined;
  if (sourceString.stringKey) {
    stringKey = {
      locale: sourceString.stringKey.locale,
      stringName: sourceString.stringKey.stringName,
      stringGroupName: sourceString.stringKey.stringGroupName,
      stringType: sourceString.stringKey.stringType,
      variant: variantKey,
    };
  }

  let content = undefined;
  if (sourceString.headRevision?.content) {
    const cleanMetadata = removeField(
      sourceString.headRevision.content.metadata,
      '__typename'
    );
    content = {
      format: sourceString.headRevision.content.format,
      metadata: cleanMetadata,
      note: editedContent
        ? editedContent.context
        : sourceString.headRevision.content.note,
      resourceContentType:
        sourceString.headRevision.content.resourceContentType,
      translate: sourceString.headRevision.content.translate,
      value: editedContent
        ? editedContent.sourceText
        : sourceString.headRevision.content.value,
    };
  }

  let headRevision = undefined;
  if (sourceString.headRevision) {
    headRevision = {
      content: content,
      createTimestamp: editedContent
        ? Date.now().toString()
        : sourceString.headRevision.createTimestamp,
      createdBy: editedContent ? reviewer : sourceString.headRevision.createdBy,
      reviewComment: editedContent
        ? editedContent.reviewComment
        : sourceString.headRevision.reviewComment,
      sourceReviewStatus: 'EDITED',
      version: sourceString.headRevision.version,
    };
  }

  let variants = undefined;
  if (sourceString.variants) {
    variants = sourceString.variants.map((variants) => {
      if (!variants) return null;
      return variants.map((variant) => {
        if (!variant) return null;
        return {
          key: variant.key,
          value: variant.value,
        };
      });
    });
  }

  return {
    stringKey: stringKey,
    headRevision: headRevision,
    variants: variants,
    requestId: sourceString.requestId,
  };
}

/**
 * Removes a specified field from an object
 * @param {Object} obj - The object to remove the field from
 * @param {string} fieldToRemove - The name of the field to remove
 * @returns {Object} A new object without the specified field
 */
function removeField(obj: any, fieldToRemove: string) {
  if (!obj) return obj;

  const result = { ...obj };
  delete result[fieldToRemove];
  return result;
}

function isStepOneReview(stringGroup: StringGroup): boolean {
  return (
    stringGroup.reviewType === ReviewType.TwoStepReview &&
    stringGroup.approvalStatus === ApprovalStatus.None
  );
}

export {
  toSourceStringInputs,
  variantToString,
  getStringPK,
  getSourceStringPK,
  isInDevStage,
  isStepOneReview,
};
